import { Controller } from "stimulus";
import { EventGeckoModalOpened } from "../../constants/events";

const MAXIMUM_PREVIOUS_DAYS_LOADED = 28;
const NUMBER_PREVIOUS_DAYS_TO_LOAD = 5;
const COIN_INSIGHTS_MODAL_ID = "coin_insights"

export default class extends Controller {
  static targets = [
    // Content containers
    "container", "recentTimeline", "previousTimeline",

    // Dummy content, infinite scrolling, ajax
    "skeletonTimeline", "noContent"
  ];

  connect() {
    this.observer = new IntersectionObserver(this._handleIntersection.bind(this), {
      root: null,
      rootMargin: '0px',
      threshold: 0.15 // If target is 15% visible
    });
    this.observer.observe(this.skeletonTimelineTarget);

    this.element.addEventListener(EventGeckoModalOpened(COIN_INSIGHTS_MODAL_ID), () => this.handleOpened());
  }

  _handleIntersection(entries, observer) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.isLoading && this.hasNext) {
        this._loadCoinInsights();
      } else if (!entry.isIntersecting && !this.isLoading && this.hasNext) {
        this.skeletonTimelineTarget.classList.add("tw-h-32");
      }
    });
  }

  handleOpened() {
    this.recentTimelineTargets.forEach(target => target.remove());
    this.previousTimelineTargets.forEach(target => target.remove());

    this.initialized = false;
    this.hasNext = true;
    this.isLoading = false;
    this.isRecent = true;

    this.referenceDate = (new Date()).toISOString().slice(0, 10);
    this.recentTimelinePage = 1;
    this.previousTimelineDate = new Date();
    this.previousTimelineDate.setDate(this.previousTimelineDate.getDate() - 1);
    this.previousDaysLoaded = 0;

    this.noContentTarget.classList.add("!tw-hidden");
    this.skeletonTimelineTarget.classList.remove("!tw-hidden", "tw-h-32");

    this._loadCoinInsights();
  }

  async _loadCoinInsights() {
    this.isLoading = true;

    // If the date has changed, reset the modal view and force a reload to ensure view consistency.
    const currentDate = (new Date()).toISOString().slice(0, 10)
    if (this.referenceDate !== currentDate) {
      this.handleOpened();
      return;
    }

    const url = new URL(this.element.dataset.coinInsightsUrl);

    if (this.isRecent) {
      url.searchParams.set("date", currentDate);
      url.searchParams.set("page", this.recentTimelinePage);

      this.recentTimelinePage += 1;
    } else {
      url.searchParams.set("date", this.previousTimelineDate.toISOString().slice(0, 10));
      url.searchParams.set("days", NUMBER_PREVIOUS_DAYS_TO_LOAD);

      this.previousTimelineDate.setDate(this.previousTimelineDate.getDate() - NUMBER_PREVIOUS_DAYS_TO_LOAD);
      this.previousDaysLoaded += NUMBER_PREVIOUS_DAYS_TO_LOAD;
    }

    const response = await fetch(url);
    if (response.status === 200) {
      this._renderTimeline(await response.text());
    } else if (response.status === 204 || response.status === 404) {
      this.isRecent = false;
      this.hasNext = this.previousDaysLoaded <= MAXIMUM_PREVIOUS_DAYS_LOADED;
    } else if (response.status === 422) {
      this.hasNext = false;
    }

    if (!this.hasNext) {
      this.skeletonTimelineTarget.classList.add("!tw-hidden");
      this.noContentTarget.classList.remove("!tw-hidden");
    }

    this.isLoading = false;
    this._triggerLoadIfLoaderVisible();
  }

  _renderTimeline(html) {
    if (!this.initialized) {
      this.containerTarget.insertAdjacentHTML("beforeend", html);
      this.skeletonTimelineTarget.classList.remove("!tw-hidden", "tw-h-32");

      this.isRecent = this.hasRecentTimelineTarget;
      this.initialized = true;
      return;
    }

    if (this.isRecent) {
      const element = document.createElement("div");
      element.innerHTML = html;

      const entriesTarget = this.recentTimelineTarget.querySelector(".gecko-timeline-entries")
      element.querySelectorAll(".gecko-timeline-entry").forEach(el => entriesTarget.appendChild(el));
    } else {
      this.containerTarget.insertAdjacentHTML("beforeend", html);
    }
  }

  /**
   * Triggers a load if the loader is visible. Under the hood, this calls
   * IntersectionObserver#unobserve and IntersectionObserver#observe, which
   * causes the observer to immediately fire the callback if the target is
   * intersecting.
   *
   * @private
   */
  _triggerLoadIfLoaderVisible() {
    this.observer.unobserve(this.skeletonTimelineTarget)
    this.observer.observe(this.skeletonTimelineTarget)
  }
}
